import { throttle } from '@/utils/helpers/throttle';
import { createReactiveState, dehydrate, watch, type ReactiveStore } from '@/utils/reactive-store';

import { ApiError, ApiSystemError } from '../errors/ApiError';
import { AnalysisResult } from './AnalysisResult';
import { GeneratorState, type GeneratorStateExported } from './GeneratorUIState';
import type { TeoText } from './TeoText';

export class GeneratorUiHandler {
  public state: ReactiveStore<GeneratorState>;

  constructor(
    private text: TeoText,
    storedState: GeneratorStateExported | { state: GeneratorStateExported } | undefined = undefined,
  ) {
    if (storedState) {
      console.info('Hydrating generator state', storedState);
      this.state = createReactiveState(
        new GeneratorState('state' in storedState ? storedState.state : storedState),
      );
      console.info('Hydrated generator state', this.state);
    } else {
      this.state = createReactiveState(new GeneratorState());
    }
    watch(this.state, () => this.store());
  }

  public withInit() {
    this.init();
    return this;
  }

  public async init() {
    if (this.state.templates.available.length === 0) {
      await this.loadTemplates();
    }
  }

  public changeSelectedHistory(page: number) {
    this.state.selectedHistory = page;
    const currentHistory = this.state.currentHistory;
    const generatorResult = currentHistory.generatorResult;
    const analysisResultData = generatorResult?.analysisResult;
    const analysisConfig = generatorResult?.analysisConfig;
    this.text.updateText(generatorResult?.text.trim() ?? '', undefined, true);
    if (analysisResultData) {
      this.text.updateResult(analysisResultData, generatorResult?.text.trim() ?? '');
    }
    if (analysisConfig) {
      this.text.configuration = {
        audience: analysisConfig.audience,
        strategy: analysisConfig.strategy,
        medium: analysisConfig.medium,
        is_informal: analysisConfig.is_informal,
      };
    }
  }

  async generateText() {
    // get current history for data
    console.info('Generating text', this.state.currentHistory, this.state);
    const currentHistory = this.state.currentHistory;
    const templateId = currentHistory.template?.id;
    if (!templateId) {
      console.error('No template found for current history');
      return;
    }

    this.state.loading = true;
    const result = await this.text.core.api.getAiTextGeneration({
      evaluation_profile_id: this.text.core.staticConfig.evaluationProfile,
      template_id: templateId,
      text_content_info: currentHistory.instructions,
      selected_options: this.state.getSelectedOptionsForRequest(),
    });
    if (ApiError.matches(result)) {
      console.error('Text generation failed:', result);
      this.state.loading = false;
      return;
    }

    const newText = result.text ? result.text.trim() : '';
    currentHistory.generatorResult = result;
    // save history
    const originalHistory = this.state.history[this.state.selectedHistory];
    if (originalHistory) {
      this.state.editingHistory.push(currentHistory.clone());
      this.state.editingHistory[this.state.selectedHistory] = originalHistory.clone();
      this.state.selectedHistory = this.state.editingHistory.length - 1;
    }
    this.state.history.push(currentHistory.clone());

    // update text and tpi and config
    this.text.isEditable = true;
    this.text.updateText(newText);
    this.text.isEditable = false;
    this.text.events.triggerEvent('textChanged', newText); // external text does not trigger

    this.text.updateResult(result.analysisResult, newText);
    this.text.configuration = {
      audience: result.analysisConfig.audience,
      strategy: result.analysisConfig.strategy,
      medium: result.analysisConfig.medium,
      is_informal: result.analysisConfig.is_informal,
    };

    this.state.loading = false;
  }

  async createTextTab() {
    const newText = this.text.core.duplicateText(this.text.id);
    if (newText) {
      const analysisData = this.text.analysis?.data;
      if (analysisData) {
        newText.analysis = new AnalysisResult(analysisData, newText, this.text.text);
      }
      newText.ui.rs.view = 'performance';
      // Until we have a proper way (reactiveStore) we need to manually store the text
      newText.store();

      this.text.core.switchText(newText.id);
    }
  }

  public async loadTemplates() {
    const ts = this.state.templates;
    ts.loading = true;
    ts.error = null;
    const templates = await this.text.core.api.getGeneratorTemplates();
    if (ApiError.matches(templates) || templates.length === 0) {
      console.error('Failed to get text generator templates:', templates);
      ts.loading = false;
      ts.error =
        ApiError.matches(templates) ? templates : new ApiSystemError('No templates available.');
      return;
    }

    ts.available = templates;
    if (ts.available[0]) {
      this.selectTemplate(ts.available[0].id);
    }
    ts.loading = false;
    this.text.store();
  }

  public selectTemplate(templateId?: string) {
    if (templateId === undefined) {
      this.state.currentHistory.template = null;
      this.state.currentHistory.selectedOptions = {};
    } else {
      this.state.currentHistory.template =
        this.state.templates.available.find((t) => t.id === templateId) || null;

      if (!this.state.currentHistory.template) {
        console.error(`Template with ID ${templateId} not found.`);
        return;
      }
    }
  }

  // this is on every letter one types, so we throttle a bit.
  private store = throttle(() => this.text.store(), 500);

  toJSON() {
    if (this.text.core.dynamicConfig.options.textGeneratorEnabled) {
      const jsonObject = {
        state: dehydrate(this.state.toJSON()),
      };
      return jsonObject;
    }
    return undefined;
  }
}

export interface GeneratorUiHandlerStoredState {
  state: Partial<GeneratorState>;
}
